<template>
  <div
    class="bg-white rounded-lg border border-gray-200 p-3 mb-2 cursor-pointer hover:shadow-md transition-shadow select-none"
    :class="{ 'border-l-4 border-l-red-500': task.status === 'canceled' }"
    @click="$emit('click', task)"
  >
    <!-- Title row -->
    <div class="flex items-start justify-between gap-2 mb-1">
      <span class="font-semibold text-sm text-gray-900 leading-tight line-clamp-2 flex-1">
        {{ task.name }}
      </span>
      <div class="flex items-center gap-0.5 flex-shrink-0 -mt-0.5 -me-1">
        <el-dropdown
          v-if="statuses.length"
          trigger="click"
          @command="(status: string) => $emit('move', status)"
          @click.stop
        >
          <button
            type="button"
            class="text-gray-400 hover:text-gray-700 p-1"
            :title="t('moveTo')"
            @click.stop
          >
            <Icon icon="mdi:swap-horizontal" class="w-4 h-4" />
          </button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item disabled>{{ t('moveTo') }}</el-dropdown-item>
              <el-dropdown-item
                v-for="col in statuses"
                :key="col.status"
                :command="col.status"
                :disabled="col.status === task.status"
              >
                <div class="flex items-center gap-2">
                  <Icon :icon="col.icon" class="w-4 h-4" />
                  {{ t(col.labelKey) }}
                </div>
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
        <button
          v-if="$can('delete', '/hr/tasks')"
          class="text-red-400 hover:text-red-600 p-1 md:p-0.5 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
          :title="t('delete')"
          @click.stop="$emit('delete', task)"
        >
          <Icon icon="mdi:trash-can-outline" class="w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- Description -->
    <p v-if="task.description" class="text-xs text-gray-500 line-clamp-2 mb-2">
      {{ task.description }}
    </p>

    <!-- Date & Time + indicators row -->
    <div v-if="task.dueDate || task.commentsCount > 0 || task.attachmentsCount > 0" class="flex items-center flex-wrap gap-x-3 gap-y-1 mb-2">
      <template v-if="task.dueDate">
        <span class="flex items-center gap-1 text-xs" :class="dueDateClass">
          <Icon :icon="isOverdue ? 'mdi:calendar-alert' : 'mdi:calendar-outline'" class="w-3.5 h-3.5" />
          {{ formatDate(task.dueDate) }}
        </span>
        <span v-if="formattedTime" class="flex items-center gap-1 text-xs" :class="dueDateClass">
          <Icon icon="mdi:clock-outline" class="w-3.5 h-3.5" />
          <span :class="{ 'line-through': task.status === 'canceled' }">{{ formattedTime }}</span>
        </span>
      </template>
      <div class="ms-auto flex items-center gap-2 text-gray-400">
        <span v-if="task.commentsCount > 0" class="flex items-center gap-0.5 text-xs" :title="t('comments')">
          <Icon icon="mdi:comment-text-outline" class="w-4 h-4" />
          {{ task.commentsCount }}
        </span>
        <span v-if="task.attachmentsCount > 0" class="flex items-center gap-0.5 text-xs" :title="t('attachments')">
          <Icon icon="mdi:paperclip" class="w-4 h-4" />
          {{ task.attachmentsCount }}
        </span>
      </div>
    </div>

    <!-- Note tag -->
    <div v-if="task.noteExternalID" class="mb-2">
      <button
        @click.stop="navigateToNote"
        class="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700 border border-yellow-200 hover:bg-yellow-200 transition-colors"
      >
        <Icon icon="mdi:note-text-outline" class="w-3 h-3" />
        #{{ task.noteExternalID }}
      </button>
    </div>

    <!-- Footer: Assignees + External ID + Created By -->
    <div class="flex items-center justify-between gap-2 mt-1">
      <div class="flex items-center gap-1 min-w-0">
        <!-- Avatars -->
        <div class="flex -space-x-1">
          <template v-for="(assignee, idx) in task.assignees.slice(0, 3)" :key="assignee.id">
            <div
              v-if="idx < 2 || task.assignees.length <= 3"
              class="w-6 h-6 rounded-full border border-white overflow-hidden bg-blue-100 flex items-center justify-center flex-shrink-0"
            >
              <img
                v-if="assignee.avatar"
                :src="storagePath + '/' + assignee.avatar"
                :alt="assignee.name"
                class="w-full h-full object-cover"
              />
              <span v-else class="text-xs font-medium text-blue-600">
                {{ getInitials(assignee) }}
              </span>
            </div>
          </template>
          <div
            v-if="task.assignees.length > 3"
            class="w-6 h-6 rounded-full border border-white bg-gray-200 flex items-center justify-center flex-shrink-0"
          >
            <span class="text-xs text-gray-600">+{{ task.assignees.length - 2 }}</span>
          </div>
        </div>
        <!-- Assignee names -->
        <span class="text-xs font-medium text-blue-700 truncate">
          {{ assigneeNames }}
        </span>
      </div>
      <div class="text-end flex-shrink-0">
        <div v-if="task.externalID" class="text-xs text-gray-400">#{{ task.externalID }}</div>
        <div v-if="task.createdByUser" class="text-xs text-gray-400">
          {{ t('createdBy') }} <span class="font-medium">{{ task.createdByUser.name?.split(' ')[0] }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed } from 'vue'
import { Icon } from '@iconify/vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useSettingsStore } from '@/stores/settingsStore'
import Task from '@/models/hr/Task'
import { Route } from '@/router/routes'
import dayjs from 'dayjs'

export default defineComponent({
  name: 'TaskCard',
  components: { Icon },
  props: {
    task: {
      type: Object as PropType<Task>,
      required: true
    },
    statuses: {
      type: Array as PropType<{ status: string; labelKey: string; icon: string }[]>,
      default: () => []
    }
  },
  emits: ['click', 'delete', 'move'],
  setup(props) {
    const { t } = useI18n({
      messages: {
        en: {
          createdBy: 'created by', moveTo: 'Move to', delete: 'Delete', comments: 'Comments', attachments: 'Attachments',
          toDo: 'To Do', inProgress: 'In Progress', done: 'Done', canceled: 'Canceled',
        },
        ar: {
          createdBy: 'أنشأها', moveTo: 'نقل إلى', delete: 'حذف', comments: 'التعليقات', attachments: 'المرفقات',
          toDo: 'للتنفيذ', inProgress: 'قيد التنفيذ', done: 'منفَّذة', canceled: 'ملغيّة',
        }
      }
    })

    const router = useRouter()
    const settingsStore = useSettingsStore()
    const storagePath = settingsStore.storagePath

    const navigateToNote = () => {
      if (props.task.noteID) {
        router.push(Route.CUSTOMERS_NOTES_VIEW.replace(':id', props.task.noteID))
      }
    }

    const isOverdue = computed(() => {
      if (!props.task.dueDate) return false
      if (props.task.status === 'done') return false
      return dayjs(props.task.dueDate).isBefore(dayjs())
    })

    const dueDateClass = computed(() => ({
      'text-red-500': isOverdue.value,
      'text-gray-500': !isOverdue.value
    }))

    const formattedTime = computed(() => {
      if (!props.task.dueDate) return ''
      return dayjs(props.task.dueDate).format('hh:mm A')
    })

    const assigneeNames = computed(() => {
      const assignees = props.task.assignees
      if (!assignees || assignees.length === 0) return ''
      if (assignees.length <= 2) {
        return assignees.map(a => a.name || '').join(', ')
      }
      return assignees.slice(0, 2).map(a => a.name?.split(' ')[0] || '').join(', ')
    })

    const formatDate = (date: string | null) => {
      if (!date) return ''
      return dayjs(date).format('DD/MM/YYYY')
    }

    const getInitials = (user: any) => {
      if (!user.name) return '?'
      return user.name.charAt(0).toUpperCase()
    }

    return { t, storagePath, isOverdue, dueDateClass, formattedTime, assigneeNames, formatDate, getInitials, navigateToNote }
  }
})
</script>

<style scoped>
.group:hover .group-hover\:opacity-100 {
  opacity: 1;
}
</style>
